import {
    type CSSProperties,
    type FC,
    type ReactNode,
    useContext,
} from "react";
import { isRecord } from "../../../../types/internal/utility";
import { cache } from "../../../Utilities/Platform/CDN";
import { createBorderStyles } from "../../Forms/DragDrop/BorderSettingsDialog";
import { DragDropComponentEmptyContext } from "../../Forms/DragDrop/DragDropComponent";
import { DragDropDataContext } from "../../Forms/DragDrop/DragDropRenderer";
import { type ComponentOptions, DragDropImageLoadContext } from "../../Forms/DragDrop/DragDropTypes";
import { createStyleFromPadding } from "../../Forms/PaddingInput";

export interface ReceiptLogoComponentValue {
    fallback: string | null;
}

export function isReceiptLogoComponentValue(value: unknown): value is ReceiptLogoComponentValue {
    if(!isRecord(value)) {
        return false;
    }

    return "fallback" in value && (typeof value.fallback === "string" || value.fallback === null);
}

function prepareBounds(style: { height?: string; width?: string; }): {
    width?: string;
    height?: string;
} {
    let { width, height } = style;

    if(typeof width === "string" && width !== "auto") {
        const parsed = parseFloat(width);

        if(!width) {
            width = "auto";
        } else if(!isNaN(parsed)) {
            width = parsed + "px";
        }
    }

    if(typeof height === "string" && height !== "auto") {
        const parsed = parseFloat(height);

        if(!height) {
            height = "auto";
        } else if(!isNaN(parsed)) {
            height = parsed + "px";
        }
    }

    return {
        width,
        height,
    };
}

export const ReceiptLogoComponent: FC<ComponentOptions> = ({ value, style }) => {
    const callback = useContext(DragDropImageLoadContext);
    const empty = useContext(DragDropComponentEmptyContext);

    const { logo } = useContext(DragDropDataContext);

    let fallback: string | null = null;

    if(isReceiptLogoComponentValue(value)) {
        fallback = value.fallback;
    }

    const image: string | null = (typeof logo === "string" ? logo : fallback);

    let content: ReactNode;

    if(typeof image !== "string") {
        content = empty;
    } else {
        const imgStyle: CSSProperties = {
            ...prepareBounds(style),
            display  : "block",
            objectFit: "contain",
            maxWidth : "100%",
            maxHeight: "100%",
        };

        if(style.horizAlign === "center") {
            imgStyle.marginLeft = "auto";
            imgStyle.marginRight = "auto";
        } else if(style.horizAlign === "right") {
            imgStyle.marginLeft = "auto";
        } else if(style.horizAlign === "left") {
            imgStyle.marginRight = "auto";
        }

        content = (
            <img
                style={imgStyle}
                src={cache(image, "media")}
                alt={"receipt-image-" + image}
                onLoad={
                    typeof callback === "function" ? () => {
                        callback(image);
                    } : undefined
                }
            />
        );
    }

    const divStyle = {
        maxHeight : "100%",
        maxWidth  : "100%",
        overflow  : "hidden",
        background: style.background,
        ...(typeof style.padding !== "undefined" ? createStyleFromPadding(style.padding) : {}),
        ...(typeof style.border !== "undefined" ? createBorderStyles(style.border) : {}),
    };

    return (
        <div style={divStyle}>
            {content}
        </div>
    );
};
